import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { t, Trans } from '@lingui/macro';
import { useNavigate } from 'react-router-dom';
import { Checkbox, Flex, P, Table, Tag } from '@decisiv/ui-components';
import CheckCircleF from '@decisiv/iconix/lib/components/CheckCircleF';

import { VisuallyHidden } from '../../../components/StyledComponents';
import { linkSurface } from './clicks';
import {
  audienceLabel,
  circuitBreakerOpen,
  networkSummary,
  resilienceFlags,
  triggerLabels,
} from './integrationFacts';
import { ChipRow, Muted, RowLink, StatusDot, TableWrapper } from './style';

const attrs = (data) => data?.rowData?.attributes ?? {};

const SORT_VALUES = {
  name: (a) => a.name || '',
  status: (a) => (a.enabled ? '0' : '1'),
  category: (a) => a.category || '',
};

const collator = new Intl.Collator(undefined, { sensitivity: 'base' });

const rowUrl = ({ id, attributes }) => attributes.slug ?? id;
const rowHref = (row) => `/integrations/${rowUrl(row)}`;

// The "dense-list" Home layout: one integration per row, sortable by name,
// status and category. The row is the link; the name is plain text. Sorting is
// local; the page keeps its own order.
const EMPTY_SELECTION = new Set();

export default function IntegrationsTable({
  integrations,
  selectable = false,
  selectedUuids = EMPTY_SELECTION,
  selectionLimitReached = false,
  onToggleSelect = noop,
}) {
  const navigate = useNavigate();
  const [sort, setSort] = useState(null);

  const rows = useMemo(() => {
    if (!sort) return integrations;
    const value = SORT_VALUES[sort.column];
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...integrations].sort(
      (a, b) =>
        sign * collator.compare(value(a.attributes), value(b.attributes)),
    );
  }, [integrations, sort]);

  const onSort = (column) => (sortingType) => {
    if (sortingType === 'ascending') setSort({ column, direction: 'asc' });
    else if (sortingType === 'descending')
      setSort({ column, direction: 'desc' });
    else setSort(null);
  };

  const columns = [
    ...(selectable
      ? [
          {
            name: 'select',
            HeaderCell: () => (
              <VisuallyHidden>
                <Trans>Select</Trans>
              </VisuallyHidden>
            ),
            DataCell: (data) => {
              const { id } = data.rowData;
              const checked = selectedUuids.has(id);
              return (
                <Checkbox
                  name={`select-integration-${id}`}
                  aria-label={t`Select ${attrs(data).name}`}
                  value="selected"
                  checked={checked}
                  disabled={selectionLimitReached && !checked}
                  onChange={() => onToggleSelect(id)}
                />
              );
            },
          },
        ]
      : []),
    {
      name: 'status',
      title: t`STATUS`,
      sortBy: onSort('status'),
      DataCell: (data) => (
        <Flex alignItems="center" style={{ gap: '0.5rem' }}>
          <StatusDot data-enabled={Boolean(attrs(data).enabled)} aria-hidden />
          <P size="small">{attrs(data).enabled ? t`Active` : t`Inactive`}</P>
        </Flex>
      ),
    },
    {
      name: 'name',
      title: t`NAME`,
      sortBy: onSort('name'),
      DataCell: (data) => (
        <RowLink to={rowHref(data.rowData)} text={attrs(data).name} />
      ),
    },
    {
      name: 'category',
      title: t`CATEGORY`,
      sortBy: onSort('category'),
      DataCell: (data) => <P size="small">{attrs(data).category}</P>,
    },
    {
      name: 'audience',
      title: t`AUDIENCE`,
      DataCell: (data) => (
        <P size="small" color="alaskanHusky">
          {audienceLabel(attrs(data))}
        </P>
      ),
    },
    {
      name: 'trigger',
      title: t`TRIGGER`,
      DataCell: (data) => (
        <ChipRow>
          {triggerLabels(attrs(data)).map(({ id, text, detail }) => (
            <span key={id} title={detail}>
              <Tag text={text} color="information" variant="outline" />
            </span>
          ))}
        </ChipRow>
      ),
    },
    {
      name: 'service_network',
      title: t`SERVICE NETWORK`,
      DataCell: (data) => {
        const network = networkSummary(attrs(data));
        return (
          <P size="small" color="alaskanHusky">
            {network.text}
            {network.rest.length > 0 && (
              <Muted title={network.rest.join(', ')}>
                {' '}
                +{network.rest.length}
              </Muted>
            )}
          </P>
        );
      },
    },
    {
      name: 'resilience',
      title: t`RESILIENCE`,
      DataCell: (data) => (
        <ChipRow>
          {circuitBreakerOpen(attrs(data)) && (
            <Tag text={t`Circuit Breaker open`} color="danger" />
          )}
          {resilienceFlags(attrs(data)).map(({ id, text }) => (
            <Tag
              key={id}
              text={text}
              palette="pastel"
              icon={CheckCircleF}
              color="success"
            />
          ))}
        </ChipRow>
      ),
    },
  ];

  // Pointer convenience only: the name link is the accessible control.
  const rowSurface = (row) => {
    const surface = linkSurface(rowHref(row), () => navigate(rowUrl(row)));
    const onControl = (event) =>
      event.target.closest('input, button, a, label') ||
      window.getSelection()?.toString();
    return {
      onClick: (event) => !onControl(event) && surface.onClick(event),
      onAuxClick: (event) => !onControl(event) && surface.onAuxClick(event),
    };
  };

  return (
    <TableWrapper title="integrations-container" className="if-table-rows">
      <Table
        columns={columns}
        data={rows}
        kind="secondary"
        variant="compact"
        getRowKey={({ id }) => id}
        getRowAttrs={(row) => rowSurface(row)}
      />
    </TableWrapper>
  );
}

IntegrationsTable.propTypes = {
  integrations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      attributes: PropTypes.oneOfType([PropTypes.object]).isRequired,
    }),
  ).isRequired,
  selectable: PropTypes.bool,
  selectedUuids: PropTypes.instanceOf(Set),
  selectionLimitReached: PropTypes.bool,
  onToggleSelect: PropTypes.func,
};
